import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import DisclaimerFooter from './DisclaimerFooter';
import { useAuth } from '../contexts/AuthContext';
// Deliberately Home's styling: these pages are the same directory of cards,
// narrowed to one family of pages, and should not look like a different kind of
// page. Home.css is the only stylesheet any of them uses.
import '../pages/Home.css';

// The shared body of Home's sibling directories — Dashboards, Studies, FAQs.
// Each supplies its own title and list of {path, title, description, icon};
// everything else (the card markup, the access filtering, the header-clearing
// padding) lives here so the three cannot drift apart.
function PageDirectory({ title, pages, emptyText, disclaimer }) {
  const { loading, hasAccess } = useAuth();
  const mainRef = useRef(null);

  useEffect(() => {
    document.title = title;
  }, [title]);

  // The header is position:fixed on wide screens only; set the content's top
  // padding to the header's real height while it is fixed. See the same effect
  // in Home.js for why this is measured rather than declared.
  useEffect(() => {
    const header = document.querySelector('.site-header');
    const main = mainRef.current;
    if (!header || !main) return;
    const update = () => {
      if (getComputedStyle(header).position === 'fixed') {
        main.style.paddingTop = `${header.offsetHeight + 16}px`;
      } else {
        main.style.paddingTop = '';
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Show only what this visitor can actually open: a directory may list pages
  // of mixed access, and a card nobody can follow is worse than no card.
  const visiblePages = pages.filter((page) => hasAccess(page.path));

  if (loading) {
    return (
      <div className="home-page">
        <Header title={title} />
        <main className="home-content">
          <div style={{ textAlign: 'center', color: '#4b5563', padding: '3rem' }}>Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="home-page">
      <a href="#main-content" className="home-skip-link">Skip to main content</a>
      <Header title={title} />

      <main id="main-content" tabIndex={-1} className="home-content" ref={mainRef}>
        <div className="page-list">
          {visiblePages.map((page) => (
            <div key={page.path} className="page-card">
              <div className="page-icon">{page.icon}</div>
              <div className="page-info">
                <h2 className="page-card-title">{page.title}</h2>
                <p>{page.description}</p>
                <div className="page-links">
                  <Link to={page.path} className="view-link">View Page</Link>
                  <button
                    className="copy-link"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + page.path);
                      alert('Link copied to clipboard!');
                    }}
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          ))}
          {visiblePages.length === 0 && (
            <p className="directory-empty">{emptyText}</p>
          )}
        </div>

        {disclaimer && <DisclaimerFooter>{disclaimer}</DisclaimerFooter>}
      </main>
    </div>
  );
}

export default PageDirectory;
